import mockData from './collections-mock.json';
import { XAT_API } from '@/services/api-paths';

export type PowerCollection = {
  required: string[];
  pawns: string[];
};

export type CollectionListItem = {
  id: number;
  name: string;
  price: { fairtrade_min: number; fairtrade_max: number };
  total: { required: number; pawns: number };
  released: string;
  released_int?: number;
};

const COLLECTION_LIST = mockData.list as CollectionListItem[];
const COLLECTION_DETAILS = mockData.details as Record<string, PowerCollection>;

export function parseCollection(raw: unknown): PowerCollection | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const required = Array.isArray(data.required) ? data.required.map(String) : [];
  const pawns = Array.isArray(data.pawns) ? data.pawns.map(String) : [];
  if (required.length === 0 && pawns.length === 0) return undefined;
  return { required, pawns };
}

export function isCollectionPower(power: { category: string; slug: string }): boolean {
  return power.category === 'collection' || Boolean(COLLECTION_DETAILS[power.slug.toLowerCase()]);
}

export function getCollectionListItem(slug: string): CollectionListItem | undefined {
  const key = slug.toLowerCase();
  return COLLECTION_LIST.find((item) => item.name === key);
}

export function getCollectionDetails(slug: string): PowerCollection | undefined {
  return COLLECTION_DETAILS[slug.toLowerCase()];
}

export function resolveCollectionForPower(power: {
  slug: string;
  collection?: PowerCollection;
}): PowerCollection | null {
  if (power.collection?.required.length || power.collection?.pawns.length) {
    return power.collection;
  }
  return getCollectionDetails(power.slug) ?? null;
}

export function enrichPowerWithCollection<T extends { slug: string; collection?: PowerCollection }>(
  power: T
): T {
  const collection = power.collection ?? getCollectionDetails(power.slug);
  if (!collection) return power;
  return { ...power, collection };
}

export function getCollectionList(): CollectionListItem[] {
  return COLLECTION_LIST;
}

export async function fetchCollectionList(): Promise<CollectionListItem[]> {
  try {
    const res = await fetch(XAT_API.collectionlist(), { cache: 'default' });
    if (!res.ok) throw new Error('Failed');
    const data = await res.json();
    if (data?.code === 200 && Array.isArray(data.powers)) {
      return data.powers as CollectionListItem[];
    }
  } catch {
    /* fallback */
  }
  return COLLECTION_LIST;
}

export function formatPowerSlugLabel(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}
